/**
 * Pawsome Ventures — Travel Details Intake Form (v2)
 * Route: /intake/:token
 *
 * Multi-step wizard, mobile-first, on-brand.
 * v2 changes (client feedback):
 *  • Multiple consignors + consignees
 *  • Structured addresses (Street/Apt/City/State/Country/Postcode)
 *  • In-transit addresses (max 2, with "Not applicable" quick fill)
 *  • Owner travel questions (same flight? within 5 days?)
 *  • Return travel date (only if Return trip)
 *  • Crate status → simple Yes/No radio (own vs. need guidance)
 *  • Titre testing "N/A — no test done" option
 *  • Medications field renamed schedule → instructions
 *  • Removed: food-travels-with-pet, destination vet, fit-to-fly checkbox
 *  • Intro blurb with logo at top (KMAN-Vets style)
 *  • Progress indicator at top AND bottom of each step
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  PawPrint, Loader2, ChevronRight, ChevronLeft, Plus, Trash2, Upload,
  CheckCircle2, X, ClipboardList,
} from 'lucide-react';
import { COUNTRIES, CITIES_BY_COUNTRY } from '../lib/countries';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOTAL_STEPS = 6;

// ---- helpers ---------------------------------------------------------
function formatSavedAt(iso) {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffSec = Math.floor((now - then) / 1000);
    if (diffSec < 5) return 'just now';
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
    if (diffSec < 86400) {
      const d = new Date(iso);
      return `at ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    const d = new Date(iso);
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
      ' at ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return '';
  }
}

// ---- default factories -----------------------------------------------
const emptyAddress = () => ({ street: '', apt: '', city: '', state_region: '', country: '', postcode: '' });
const emptyContact = () => ({ full_name: '', phone: '', email: '', alt_contact: '', address: emptyAddress() });
const emptyPet = () => ({
  existing_pet_id: null,
  name: '', species: 'Dog', breed: '', sex_status: 'Female Spayed',
  dob: '', weight: '', colour: '', microchip_number: '',
  photo_path: '',
  cayman_passport: { holder: false, number: '', issue_or_renewal_date: '' },
  vaccinations: { felv_date: '', fvrcp_date: '', dhpp_date: '', leptospirosis_date: '', bordetella_date: '', notes: '' },
  rabies: { last_vaccine_date: '', administered_by: '', upload_paths: [], upload_names: [] },
  titre: { no_titre_test: false, last_test_date: '', drawn_at: '', reference_lab: '', upload_paths: [], upload_names: [] },
  medications: [],
  allergies: '',
  temperament: { tags: [], notes: '' },
  feeding: { brand: '', schedule: '', quantity: '', restrictions: '' },
  notes: '',
});

const TEMPERAMENT_OPTIONS = [
  'Friendly', 'Anxious', 'Aggressive with strangers',
  'Aggressive with other animals', 'Motion sickness history', 'Prior sedation',
];

const STEP_TITLES = ['Consignor', 'Consignee', 'Travel', 'Pets', 'Care', 'Sign'];

// ==============================================================
export default function IntakeForm() {
  const { token } = useParams();
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    consignors: [emptyContact()],
    consignees: [emptyContact()],
    travel: {
      one_way_or_return: 'One-way',
      departure_date: '',
      return_travel_date: '',
      departure_airport: '',
      arrival_airport: '',
      airline: '',
      flight_number: '',
      owner_same_flight: '',    // 'yes' | 'no' | ''
      owner_within_5_days: '',  // 'yes' | 'no' | ''
      crate_status: '',         // 'have' | 'need_guidance' | ''
      in_transit_not_applicable: true,
      in_transit_addresses: [],
    },
    pets: [emptyPet()],
    emergency_contact: { name: '', phone: '', email: '', relationship: '' },
    confirmations: { info_accurate: false, authorises_pawsome: false },
    signature: { typed_name: '' },
  });

  // Auto-save the draft to the backend every 30 seconds so a lost
  // connection / accidental refresh doesn't destroy the client's progress.
  const [lastSavedAt, setLastSavedAt] = useState(null);
  useEffect(() => {
    if (loading || done || !link) return;
    const iv = setInterval(async () => {
      try {
        const { data } = await axios.post(`${API}/intake/${token}/draft`, { form });
        if (data.saved) setLastSavedAt(data.at);
      } catch { /* silent — retry next tick */ }
    }, 30000);
    return () => clearInterval(iv);
  }, [form, loading, done, link, token]);

  useEffect(() => {
    axios.get(`${API}/intake/${token}`)
      .then(({ data }) => {
        setLink(data);
        // If there's a saved draft, resume from there — it takes priority over prefill
        if (data.draft && Object.keys(data.draft).length > 0) {
          setForm((f) => ({ ...f, ...data.draft }));
          setLastSavedAt(data.draft_saved_at);
          setLoading(false);
          return;
        }
        setForm((f) => {
          const next = { ...f };
          if (data.client_name || data.client_email) {
            next.consignors = [{
              ...emptyContact(),
              full_name: data.client_name || '',
              email: data.client_email || '',
            }];
          }
          const prefillPets = (data.prefill?.pets) || [];
          if (prefillPets.length > 0) {
            next.pets = prefillPets.map((p) => ({
              ...emptyPet(),
              existing_pet_id: p.existing_pet_id || null,
              name: p.name || '',
              species: p.species || 'Dog',
              breed: p.breed || '',
              sex_status: p.sex_status || 'Unknown',
              dob: p.dob || '',
              weight: p.weight || '',
              colour: p.colour || '',
              microchip_number: p.microchip_number || '',
            }));
          }
          return next;
        });
        setLoading(false);
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || 'Link is not available.');
        setLoading(false);
      });
  }, [token]);

  // ---- generic set helpers -------------------------------------------
  const setPath = (path, value) => setForm((prev) => {
    const next = structuredClone(prev);
    const keys = path.split('.');
    let cur = next;
    for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
    cur[keys[keys.length - 1]] = value;
    return next;
  });
  const setListItem = (list, idx, key, value) => setForm((prev) => {
    const next = structuredClone(prev);
    const keys = key.split('.');
    let cur = next[list][idx];
    for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
    cur[keys[keys.length - 1]] = value;
    return next;
  });

  const addConsignor = () => setForm(f => ({ ...f, consignors: [...f.consignors, emptyContact()] }));
  const removeConsignor = (i) => setForm(f => ({ ...f, consignors: f.consignors.filter((_, x) => x !== i) }));
  const addConsignee = () => setForm(f => ({ ...f, consignees: [...f.consignees, emptyContact()] }));
  const removeConsignee = (i) => setForm(f => ({ ...f, consignees: f.consignees.filter((_, x) => x !== i) }));
  const addPet = () => setForm(f => ({ ...f, pets: [...f.pets, emptyPet()] }));
  const removePet = (i) => setForm(f => ({ ...f, pets: f.pets.filter((_, x) => x !== i) }));

  const toggleInTransitNA = (val) => setForm((f) => ({
    ...f,
    travel: {
      ...f.travel,
      in_transit_not_applicable: val,
      in_transit_addresses: val ? [] : (f.travel.in_transit_addresses.length ? f.travel.in_transit_addresses : [emptyAddress()]),
    },
  }));
  const addInTransit = () => setForm((f) => f.travel.in_transit_addresses.length >= 2 ? f : {
    ...f, travel: { ...f.travel, in_transit_addresses: [...f.travel.in_transit_addresses, emptyAddress()] },
  });
  const removeInTransit = (i) => setForm((f) => ({
    ...f, travel: { ...f.travel, in_transit_addresses: f.travel.in_transit_addresses.filter((_, x) => x !== i) },
  }));

  // ---- upload helper -------------------------------------------------
  const uploadFile = async (petIdx, category, file) => {
    const fd = new FormData();
    fd.append('file', file); fd.append('category', category); fd.append('pet_index', String(petIdx));
    const { data } = await axios.post(`${API}/intake/${token}/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { storage_path: data.storage_path, filename: data.filename };
  };
  const attachRabies = async (petIdx, file) => {
    const { storage_path, filename } = await uploadFile(petIdx, 'rabies', file);
    setForm((prev) => {
      const n = structuredClone(prev);
      n.pets[petIdx].rabies.upload_paths.push(storage_path);
      n.pets[petIdx].rabies.upload_names.push(filename);
      return n;
    });
  };
  const attachTitre = async (petIdx, file) => {
    const { storage_path, filename } = await uploadFile(petIdx, 'titre', file);
    setForm((prev) => {
      const n = structuredClone(prev);
      n.pets[petIdx].titre.upload_paths.push(storage_path);
      n.pets[petIdx].titre.upload_names.push(filename);
      return n;
    });
  };
  const attachPhoto = async (petIdx, file) => {
    const { storage_path } = await uploadFile(petIdx, 'photo', file);
    setListItem('pets', petIdx, 'photo_path', storage_path);
  };

  // ---- validation ----------------------------------------------------
  const canProceed = useMemo(() => {
    const t = form.travel;
    if (step === 0) {
      const c = form.consignors[0] || {};
      return !!(c.full_name && c.phone && c.email);
    }
    if (step === 1) {
      const c = form.consignees[0] || {};
      return !!(c.full_name && c.phone);
    }
    if (step === 2) {
      const oneWay = t.one_way_or_return === 'One-way';
      return !!(
        t.departure_date && t.departure_airport && t.arrival_airport &&
        t.crate_status && t.owner_same_flight && t.owner_within_5_days &&
        (oneWay || t.return_travel_date)
      );
    }
    if (step === 3) return form.pets.length > 0 && form.pets.every(p => p.name && p.species);
    if (step === 4) return true;
    if (step === 5) {
      const c = form.confirmations;
      return c.info_accurate && c.authorises_pawsome && form.signature.typed_name.trim().length >= 3;
    }
    return true;
  }, [step, form]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        signature: {
          ...form.signature,
          signed_at: new Date().toISOString(),
          user_agent: (typeof navigator !== 'undefined' ? navigator.userAgent : ''),
        },
      };
      await axios.post(`${API}/intake/${token}`, payload);
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- gated render states ------------------------------------------
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-emerald-50"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;
  }
  if (error && !link) {
    return <div className="min-h-screen flex items-center justify-center bg-emerald-50 p-6"><div className="bg-white p-8 rounded-2xl shadow max-w-md text-center"><X className="w-12 h-12 mx-auto text-red-500 mb-3" /><h1 className="text-xl font-semibold mb-2">Link unavailable</h1><p className="text-gray-600">{error}</p></div></div>;
  }
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-emerald-50 p-6">
        <div className="bg-white p-10 rounded-3xl shadow-lg max-w-lg text-center">
          <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-600 mb-4" />
          <h1 className="text-2xl font-bold mb-3 text-gray-900">Thank you!</h1>
          <p className="text-gray-600 mb-2">Your travel details have been sent through to the Pawsome Ventures team.</p>
          <p className="text-sm text-gray-500">We'll be in touch shortly with next steps.</p>
        </div>
      </div>
    );
  }

  // ---- render --------------------------------------------------------
  const progressPct = Math.round(((step + 1) / TOTAL_STEPS) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 pb-24">
      <header className="bg-white border-b border-emerald-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <PawPrint className="w-7 h-7 text-emerald-600" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-gray-900 truncate">Pawsome Ventures — Travel Intake</div>
            <div className="text-xs text-gray-500 truncate">Step {step + 1} of {TOTAL_STEPS} · {STEP_TITLES[step]} · {progressPct}%</div>
          </div>
        </div>
        <div className="h-1 bg-emerald-100">
          <div className="h-full bg-emerald-600 transition-all" style={{ width: `${progressPct}%` }} />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="intake-form">
        {/* Intro blurb — shown on first step only, KMAN-Vets-style */}
        {step === 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <img src="/pawsome-logo.jpg" alt="Pawsome Ventures" className="w-14 h-14 rounded-xl object-cover flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Welcome to your travel intake form</h1>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                  This form gathers everything we need to book your pet's flight, prepare the paperwork,
                  and coordinate with the airline and destination vet. It usually takes about <b>10 minutes</b>.
                  You can save as you go — nothing is submitted until you sign on the last page.
                </p>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                  Fields marked <span className="text-red-500 font-semibold">*</span> are required. Everything else can be left
                  blank and we'll follow up if we need more detail.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* STEP 0 — Consignors */}
        {step === 0 && (
          <div className="space-y-4">
            {form.consignors.map((c, i) => (
              <Section
                key={i}
                title={`Consignor ${i + 1}${i === 0 ? ' (primary shipper)' : ''}`}
                subtitle="Person sending the pet(s)."
                right={form.consignors.length > 1 && <button onClick={() => removeConsignor(i)} className="text-red-500 hover:text-red-700 p-1"><Trash2 className="w-4 h-4" /></button>}
              >
                <ContactFields prefix={`consignors.${i}`} value={c} setListItem={(key, v) => setListItem('consignors', i, key, v)} required={i === 0} testIdPrefix={`consignor-${i}`} />
              </Section>
            ))}
            <button onClick={addConsignor} className="w-full py-3 border-2 border-dashed border-emerald-300 rounded-xl text-emerald-700 font-semibold hover:bg-emerald-50 flex items-center justify-center gap-2" data-testid="add-consignor-btn">
              <Plus className="w-4 h-4" /> Add another consignor
            </button>
          </div>
        )}

        {/* STEP 1 — Consignees */}
        {step === 1 && (
          <div className="space-y-4">
            {form.consignees.map((c, i) => (
              <Section
                key={i}
                title={`Consignee ${i + 1}${i === 0 ? ' (primary receiver)' : ''}`}
                subtitle="Person collecting the pet at destination."
                right={form.consignees.length > 1 && <button onClick={() => removeConsignee(i)} className="text-red-500 hover:text-red-700 p-1"><Trash2 className="w-4 h-4" /></button>}
              >
                <ContactFields prefix={`consignees.${i}`} value={c} setListItem={(key, v) => setListItem('consignees', i, key, v)} required={i === 0} phoneOnly={i === 0} testIdPrefix={`consignee-${i}`} />
              </Section>
            ))}
            <button onClick={addConsignee} className="w-full py-3 border-2 border-dashed border-emerald-300 rounded-xl text-emerald-700 font-semibold hover:bg-emerald-50 flex items-center justify-center gap-2" data-testid="add-consignee-btn">
              <Plus className="w-4 h-4" /> Add another consignee
            </button>
          </div>
        )}

        {/* STEP 2 — Travel + owner questions + in-transit + crate */}
        {step === 2 && (
          <div className="space-y-4">
            <Section title="Travel details" subtitle="Flight info — we'll help finalise anything you're not sure about.">
              <Grid2>
                <Field label="One-way or Return *"><Select value={form.travel.one_way_or_return} onChange={e => setPath('travel.one_way_or_return', e.target.value)} options={['One-way', 'Return']} /></Field>
                <Field label="Departure Date *"><Input type="date" value={form.travel.departure_date} onChange={e => setPath('travel.departure_date', e.target.value)} data-testid="departure-date" /></Field>
                {form.travel.one_way_or_return === 'Return' && (
                  <Field label="Return travel date *" full><Input type="date" value={form.travel.return_travel_date} onChange={e => setPath('travel.return_travel_date', e.target.value)} data-testid="return-date" /></Field>
                )}
                <Field label="Departure Airport / City *"><Input value={form.travel.departure_airport} onChange={e => setPath('travel.departure_airport', e.target.value)} placeholder="GCM · George Town" /></Field>
                <Field label="Arrival Airport / City *"><Input value={form.travel.arrival_airport} onChange={e => setPath('travel.arrival_airport', e.target.value)} placeholder="MIA · Miami" /></Field>
                <Field label="Airline"><Input value={form.travel.airline} onChange={e => setPath('travel.airline', e.target.value)} placeholder="Cayman Airways" /></Field>
                <Field label="Flight #"><Input value={form.travel.flight_number} onChange={e => setPath('travel.flight_number', e.target.value)} placeholder="KX 100" /></Field>
              </Grid2>
            </Section>

            <Section title="Owner travel" subtitle="Airlines and border officials sometimes ask.">
              <YesNo label="Is the owner travelling on the same flight as the pet? *" value={form.travel.owner_same_flight} onChange={v => setPath('travel.owner_same_flight', v)} testId="owner-same-flight" />
              <YesNo label="Is the owner travelling within 5 days of the pet? *" value={form.travel.owner_within_5_days} onChange={v => setPath('travel.owner_within_5_days', v)} testId="owner-within-5" />
            </Section>

            <Section title="In-transit addresses" subtitle="Any address the pet will pass through en route (max 2).">
              <Checkbox
                checked={form.travel.in_transit_not_applicable}
                onChange={toggleInTransitNA}
                label="Not applicable — the pet flies straight through with no in-transit address"
                dataTestId="in-transit-na"
              />
              {!form.travel.in_transit_not_applicable && (
                <div className="space-y-4 mt-4">
                  {form.travel.in_transit_addresses.map((a, i) => (
                    <div key={i} className="border border-emerald-100 rounded-xl p-4 relative">
                      <div className="text-xs font-semibold text-emerald-700 mb-3">Address {i + 1}</div>
                      <AddressFields
                        value={a}
                        onChange={(key, v) => setForm(f => {
                          const next = structuredClone(f);
                          next.travel.in_transit_addresses[i][key] = v;
                          return next;
                        })}
                        testIdPrefix={`in-transit-${i}`}
                      />
                      <button onClick={() => removeInTransit(i)} className="absolute top-2 right-2 text-red-500 hover:text-red-700 p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  {form.travel.in_transit_addresses.length < 2 && (
                    <button onClick={addInTransit} className="text-sm text-emerald-700 hover:text-emerald-900 flex items-center gap-1"><Plus className="w-4 h-4" /> Add in-transit address</button>
                  )}
                </div>
              )}
            </Section>

            <Section title="Travel crate" subtitle="IATA-approved crates are required for air travel.">
              <RadioGroup
                value={form.travel.crate_status}
                onChange={v => setPath('travel.crate_status', v)}
                options={[
                  { value: 'have', label: 'I have an IATA-approved travel crate/carrier for each pet' },
                  { value: 'need_guidance', label: 'I need guidance on procuring an IATA-approved travel crate/carrier for each pet' },
                ]}
                testId="crate-status"
              />
            </Section>
          </div>
        )}

        {/* STEP 3 — Pets */}
        {step === 3 && (
          <div className="space-y-4">
            {form.pets.map((pet, i) => (
              <Section
                key={i}
                title={`Pet ${i + 1}${pet.name ? ` — ${pet.name}` : ''}`}
                subtitle="Basic details, Cayman passport, and vaccinations."
                right={form.pets.length > 1 && <button onClick={() => removePet(i)} className="text-red-500 hover:text-red-700 p-1"><Trash2 className="w-4 h-4" /></button>}
              >
                <Grid2>
                  <Field label="Pet Name *"><Input value={pet.name} onChange={e => setListItem('pets', i, 'name', e.target.value)} data-testid={`pet-${i}-name`} /></Field>
                  <Field label="Species *"><Select value={pet.species} onChange={e => setListItem('pets', i, 'species', e.target.value)} options={['Dog', 'Cat', 'Other']} /></Field>
                  <Field label="Breed"><Input value={pet.breed} onChange={e => setListItem('pets', i, 'breed', e.target.value)} /></Field>
                  <Field label="Sex & Status"><Select value={pet.sex_status} onChange={e => setListItem('pets', i, 'sex_status', e.target.value)} options={['Female Spayed', 'Female Intact', 'Male Neutered', 'Male Intact', 'Unknown']} /></Field>
                  <Field label="Date of Birth"><Input type="date" value={pet.dob} onChange={e => setListItem('pets', i, 'dob', e.target.value)} /></Field>
                  <Field label="Weight"><Input value={pet.weight} onChange={e => setListItem('pets', i, 'weight', e.target.value)} placeholder="lbs or kg" /></Field>
                  <Field label="Colour / Markings"><Input value={pet.colour} onChange={e => setListItem('pets', i, 'colour', e.target.value)} /></Field>
                  <Field label="Microchip Number"><Input value={pet.microchip_number} onChange={e => setListItem('pets', i, 'microchip_number', e.target.value)} /></Field>
                  <Field label="Photo" full>
                    <FileInput onFile={(f) => attachPhoto(i, f)} current={pet.photo_path ? 'Photo uploaded ✓' : ''} accept="image/*" />
                  </Field>
                </Grid2>

                <Subheader>Cayman Islands Pet Passport</Subheader>
                <Grid2>
                  <Field label="Passport Holder">
                    <Checkbox checked={!!pet.cayman_passport.holder} onChange={v => setListItem('pets', i, 'cayman_passport.holder', v)} label="Yes, this pet has a CI passport" />
                  </Field>
                  <Field label="Passport #"><Input value={pet.cayman_passport.number} onChange={e => setListItem('pets', i, 'cayman_passport.number', e.target.value)} disabled={!pet.cayman_passport.holder} /></Field>
                  <Field label="Issue / Last Renewal Date" full><Input type="date" value={pet.cayman_passport.issue_or_renewal_date} onChange={e => setListItem('pets', i, 'cayman_passport.issue_or_renewal_date', e.target.value)} disabled={!pet.cayman_passport.holder} /></Field>
                </Grid2>

                <Subheader>Vaccinations</Subheader>
                {(pet.species || '').toLowerCase() === 'dog' && (
                  <Grid2>
                    <Field label="DHPP Date"><Input type="date" value={pet.vaccinations.dhpp_date} onChange={e => setListItem('pets', i, 'vaccinations.dhpp_date', e.target.value)} /></Field>
                    <Field label="Leptospirosis Date"><Input type="date" value={pet.vaccinations.leptospirosis_date} onChange={e => setListItem('pets', i, 'vaccinations.leptospirosis_date', e.target.value)} /></Field>
                    <Field label="Bordetella / Kennel Cough Date" full><Input type="date" value={pet.vaccinations.bordetella_date} onChange={e => setListItem('pets', i, 'vaccinations.bordetella_date', e.target.value)} /></Field>
                  </Grid2>
                )}
                {(pet.species || '').toLowerCase() === 'cat' && (
                  <Grid2>
                    <Field label="FELV Date"><Input type="date" value={pet.vaccinations.felv_date} onChange={e => setListItem('pets', i, 'vaccinations.felv_date', e.target.value)} /></Field>
                    <Field label="FVRCP Date"><Input type="date" value={pet.vaccinations.fvrcp_date} onChange={e => setListItem('pets', i, 'vaccinations.fvrcp_date', e.target.value)} /></Field>
                  </Grid2>
                )}
                {!['dog', 'cat'].includes((pet.species || '').toLowerCase()) && (
                  <Field label="Vaccination Notes"><Textarea value={pet.vaccinations.notes} onChange={e => setListItem('pets', i, 'vaccinations.notes', e.target.value)} rows={2} /></Field>
                )}

                <Subheader>Rabies vaccine</Subheader>
                <Grid2>
                  <Field label="Last Vaccine Date"><Input type="date" value={pet.rabies.last_vaccine_date} onChange={e => setListItem('pets', i, 'rabies.last_vaccine_date', e.target.value)} /></Field>
                  <Field label="Administered By / Where"><Input value={pet.rabies.administered_by} onChange={e => setListItem('pets', i, 'rabies.administered_by', e.target.value)} placeholder="Vet name & clinic" /></Field>
                  <Field label="Upload rabies certificate(s) — all to date" full>
                    <FileList files={pet.rabies.upload_names} onFile={(f) => attachRabies(i, f)} />
                  </Field>
                </Grid2>

                <Subheader>Rabies titre testing</Subheader>
                <Checkbox
                  checked={!!pet.titre.no_titre_test}
                  onChange={v => setListItem('pets', i, 'titre.no_titre_test', v)}
                  label="Not applicable — this pet has not had a rabies titre test"
                  dataTestId={`pet-${i}-no-titre`}
                />
                {!pet.titre.no_titre_test && (
                  <Grid2>
                    <Field label="Last Test Date"><Input type="date" value={pet.titre.last_test_date} onChange={e => setListItem('pets', i, 'titre.last_test_date', e.target.value)} /></Field>
                    <Field label="Drawn By / Where"><Input value={pet.titre.drawn_at} onChange={e => setListItem('pets', i, 'titre.drawn_at', e.target.value)} placeholder="Vet name & clinic" /></Field>
                    <Field label="Reference Lab"><Input value={pet.titre.reference_lab} onChange={e => setListItem('pets', i, 'titre.reference_lab', e.target.value)} placeholder="Kansas State etc." /></Field>
                    <Field label="Upload titre certificate(s) — all to date" full>
                      <FileList files={pet.titre.upload_names} onFile={(f) => attachTitre(i, f)} />
                    </Field>
                  </Grid2>
                )}
              </Section>
            ))}
            <button onClick={addPet} className="w-full py-3 border-2 border-dashed border-emerald-300 rounded-xl text-emerald-700 font-semibold hover:bg-emerald-50 flex items-center justify-center gap-2" data-testid="add-pet-btn">
              <Plus className="w-4 h-4" /> Add another pet
            </button>
          </div>
        )}

        {/* STEP 4 — Care & Feeding */}
        {step === 4 && (
          <div className="space-y-4">
            {form.pets.map((pet, i) => (
              <Section key={i} title={`${pet.name || `Pet ${i + 1}`} — care & feeding`} subtitle="Medications, temperament, feeding routine, and anything else we should know.">
                <Subheader>Medications</Subheader>
                {(pet.medications || []).map((m, mi) => (
                  <div key={mi} className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-2">
                    <Input placeholder="Name" value={m.name || ''} onChange={e => {
                      const meds = [...pet.medications]; meds[mi] = { ...m, name: e.target.value }; setListItem('pets', i, 'medications', meds);
                    }} />
                    <Input placeholder="Dose" value={m.dose || ''} onChange={e => {
                      const meds = [...pet.medications]; meds[mi] = { ...m, dose: e.target.value }; setListItem('pets', i, 'medications', meds);
                    }} />
                    <Input placeholder="Instructions" value={m.instructions || ''} onChange={e => {
                      const meds = [...pet.medications]; meds[mi] = { ...m, instructions: e.target.value }; setListItem('pets', i, 'medications', meds);
                    }} />
                    <div className="flex gap-1">
                      <Input placeholder="Notes" value={m.notes || ''} onChange={e => {
                        const meds = [...pet.medications]; meds[mi] = { ...m, notes: e.target.value }; setListItem('pets', i, 'medications', meds);
                      }} />
                      <button onClick={() => setListItem('pets', i, 'medications', pet.medications.filter((_, x) => x !== mi))} className="text-red-500 hover:text-red-700 p-2"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
                <button onClick={() => setListItem('pets', i, 'medications', [...(pet.medications || []), { name: '', dose: '', instructions: '', notes: '' }])} className="text-sm text-emerald-700 hover:text-emerald-900 flex items-center gap-1"><Plus className="w-3 h-3" /> Add medication</button>

                <Subheader>Allergies</Subheader>
                <Textarea value={pet.allergies} onChange={e => setListItem('pets', i, 'allergies', e.target.value)} rows={2} placeholder="Food, medications, environmental — anything to avoid." />

                <Subheader>Temperament</Subheader>
                <div className="flex flex-wrap gap-2 mb-2">
                  {TEMPERAMENT_OPTIONS.map(t => {
                    const active = (pet.temperament.tags || []).includes(t);
                    return (
                      <button key={t} onClick={() => {
                        const tags = active ? pet.temperament.tags.filter(x => x !== t) : [...pet.temperament.tags, t];
                        setListItem('pets', i, 'temperament.tags', tags);
                      }} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${active ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-300'}`}>
                        {t}
                      </button>
                    );
                  })}
                </div>
                <Textarea value={pet.temperament.notes} onChange={e => setListItem('pets', i, 'temperament.notes', e.target.value)} rows={2} placeholder="Any additional notes — quirks, triggers, things that soothe them." />

                <Subheader>Feeding</Subheader>
                <Grid2>
                  <Field label="Food Brand"><Input value={pet.feeding.brand} onChange={e => setListItem('pets', i, 'feeding.brand', e.target.value)} /></Field>
                  <Field label="Schedule"><Input value={pet.feeding.schedule} onChange={e => setListItem('pets', i, 'feeding.schedule', e.target.value)} placeholder="e.g. 8am & 6pm" /></Field>
                  <Field label="Quantity per meal"><Input value={pet.feeding.quantity} onChange={e => setListItem('pets', i, 'feeding.quantity', e.target.value)} placeholder="e.g. 1 cup" /></Field>
                  <Field label="Dietary Restrictions"><Input value={pet.feeding.restrictions} onChange={e => setListItem('pets', i, 'feeding.restrictions', e.target.value)} placeholder="e.g. grain-free" /></Field>
                </Grid2>

                <Subheader>Additional notes</Subheader>
                <Textarea value={pet.notes} onChange={e => setListItem('pets', i, 'notes', e.target.value)} rows={3} placeholder="Anything else we should know — behaviour, health history, past travel experiences." />
              </Section>
            ))}
          </div>
        )}

        {/* STEP 5 — Confirmations + signature */}
        {step === 5 && (
          <div className="space-y-4">
            <Section title="Emergency contact during travel" subtitle="Someone we can reach if we can't get you.">
              <Grid2>
                <Field label="Name"><Input value={form.emergency_contact.name} onChange={e => setPath('emergency_contact.name', e.target.value)} /></Field>
                <Field label="Relationship"><Input value={form.emergency_contact.relationship} onChange={e => setPath('emergency_contact.relationship', e.target.value)} placeholder="e.g. Spouse, sibling" /></Field>
                <Field label="Phone"><Input value={form.emergency_contact.phone} onChange={e => setPath('emergency_contact.phone', e.target.value)} /></Field>
                <Field label="Email"><Input value={form.emergency_contact.email} onChange={e => setPath('emergency_contact.email', e.target.value)} /></Field>
              </Grid2>
            </Section>

            <Section title="Confirmations & signature" subtitle="Please read and tick each box, then sign.">
              <div className="space-y-2 mb-4">
                <Checkbox checked={form.confirmations.info_accurate} onChange={v => setPath('confirmations.info_accurate', v)} label="All information provided in this form is accurate to the best of my knowledge." dataTestId="confirm-accurate" />
                <Checkbox checked={form.confirmations.authorises_pawsome} onChange={v => setPath('confirmations.authorises_pawsome', v)} label="I authorise Pawsome Ventures to act on my behalf with airlines, vets, and destination authorities." dataTestId="confirm-authorise" />
              </div>
              <div className="border-t border-emerald-100 pt-4">
                <label className="block text-sm font-semibold text-emerald-700 mb-1">Type your full legal name to sign *</label>
                <Input value={form.signature.typed_name} onChange={e => setPath('signature.typed_name', e.target.value)} placeholder="Full name" data-testid="signature-input" />
                {form.signature.typed_name.trim().length >= 3 && (
                  <div className="mt-4 border border-emerald-100 rounded-xl p-4 bg-emerald-50/50">
                    <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-1">Preview — DocuSign-style signature</div>
                    <div style={{ fontFamily: '"Dancing Script", cursive', fontSize: '32px', color: '#1f2937' }}>{form.signature.typed_name.split(',')[0].trim()}</div>
                    <div className="border-t border-gray-300 my-2" />
                    <div className="text-sm font-semibold text-gray-800">{form.signature.typed_name}</div>
                    <div className="text-xs text-gray-500 italic mt-1">To be digitally signed on submission · document hash will be generated</div>
                  </div>
                )}
              </div>
            </Section>
            {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm">{error}</div>}
          </div>
        )}

        {/* Bottom progress + navigation */}
        <div className="space-y-3 pt-4">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <ClipboardList className="w-3.5 h-3.5" />
            <span>{progressPct}% complete — {STEP_TITLES[step]} · Step {step + 1} of {TOTAL_STEPS}</span>
            {lastSavedAt && (
              <span className="ml-auto flex items-center gap-1 text-emerald-700" data-testid="intake-saved-chip">
                <CheckCircle2 className="w-3 h-3" /> Saved · {formatSavedAt(lastSavedAt)}
              </span>
            )}
          </div>
          <div className="h-1 bg-emerald-100 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="flex items-center justify-between">
            <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} className="px-5 py-3 rounded-xl border-2 border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2" data-testid="prev-btn">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            {step < TOTAL_STEPS - 1 ? (
              <button onClick={() => setStep(s => Math.min(TOTAL_STEPS - 1, s + 1))} disabled={!canProceed} className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm" data-testid="next-btn">
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={submit} disabled={!canProceed || submitting} className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm" data-testid="submit-intake-btn">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Submit intake form</>}
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

// ==============================================================
// Presentational sub-components
// ==============================================================
const Section = ({ title, subtitle, right, children }) => (
  <section className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-5 sm:p-6">
    <div className="flex items-start justify-between gap-2 mb-4">
      <div>
        <h2 className="text-lg font-bold text-gray-900">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </section>
);
const Subheader = ({ children }) => (
  <div className="border-t border-emerald-100 mt-4 pt-4"><h4 className="font-semibold text-sm text-emerald-700 mb-2">{children}</h4></div>
);
const Grid2 = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
const Field = ({ label, children, full }) => (
  <label className={`block ${full ? 'sm:col-span-2' : ''}`}>
    <span className="block text-xs font-semibold text-gray-600 mb-1">{label}</span>
    {children}
  </label>
);
const Input = (props) => <input {...props} className={`w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none disabled:bg-gray-50 disabled:text-gray-400 ${props.className || ''}`} />;
const Textarea = (props) => <textarea {...props} className={`w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none ${props.className || ''}`} />;
const Select = ({ value, onChange, options }) => (
  <select value={value} onChange={onChange} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none bg-white">
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);
const Checkbox = ({ checked, onChange, label, dataTestId }) => (
  <label className="flex items-start gap-2 cursor-pointer text-sm text-gray-800">
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" data-testid={dataTestId} />
    <span>{label}</span>
  </label>
);
const YesNo = ({ label, value, onChange, testId }) => (
  <div className="mb-3">
    <div className="text-sm text-gray-800 mb-2">{label}</div>
    <div className="flex gap-2">
      {['yes', 'no'].map(opt => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          data-testid={`${testId}-${opt}`}
          className={`px-4 py-2 rounded-lg text-sm font-medium border-2 ${value === opt ? 'border-emerald-600 bg-emerald-50 text-emerald-800' : 'border-gray-200 text-gray-600 hover:border-emerald-300'}`}
        >
          {opt === 'yes' ? 'Yes' : 'No'}
        </button>
      ))}
    </div>
  </div>
);
const RadioGroup = ({ value, onChange, options, testId }) => (
  <div className="space-y-2">
    {options.map(o => (
      <label key={o.value} className="flex items-start gap-2 cursor-pointer p-3 rounded-lg border-2 hover:border-emerald-300"
        style={{ borderColor: value === o.value ? '#059669' : '#e5e7eb', background: value === o.value ? '#ecfdf5' : 'white' }}>
        <input type="radio" checked={value === o.value} onChange={() => onChange(o.value)} className="mt-0.5" data-testid={`${testId}-${o.value}`} />
        <span className="text-sm text-gray-800">{o.label}</span>
      </label>
    ))}
  </div>
);
const FileInput = ({ onFile, current, accept }) => (
  <div className="flex items-center gap-2">
    <label className="flex-1 cursor-pointer flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50 text-sm text-gray-600">
      <Upload className="w-4 h-4 text-emerald-600" />
      {current || 'Tap to upload'}
      <input type="file" className="hidden" accept={accept} onChange={e => { if (e.target.files?.[0]) onFile(e.target.files[0]); }} />
    </label>
  </div>
);
const FileList = ({ files, onFile }) => (
  <div>
    {(files || []).map((n, i) => (
      <div key={i} className="text-sm text-emerald-800 bg-emerald-50 rounded-lg px-3 py-1.5 mb-1 flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4 text-emerald-600" /> {n}
      </div>
    ))}
    <FileInput onFile={onFile} current={files?.length ? `Add another (${files.length} uploaded)` : ''} />
  </div>
);
const AddressFields = ({ value, onChange, testIdPrefix }) => {
  const cityOptionsId = `${testIdPrefix}-city-options`;
  const countryOptionsId = `${testIdPrefix}-country-options`;
  const cityChoices = CITIES_BY_COUNTRY[value.country] || [];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Street" full>
        <Input value={value.street} onChange={e => onChange('street', e.target.value)} data-testid={`${testIdPrefix}-street`} />
      </Field>
      <Field label="Apartment / Unit / Suite">
        <Input value={value.apt} onChange={e => onChange('apt', e.target.value)} data-testid={`${testIdPrefix}-apt`} />
      </Field>
      <Field label="City / Town">
        <Input list={cityOptionsId} value={value.city} onChange={e => onChange('city', e.target.value)} data-testid={`${testIdPrefix}-city`} placeholder={cityChoices.length ? `e.g. ${cityChoices[0]}` : ''} />
        {cityChoices.length > 0 && (
          <datalist id={cityOptionsId}>
            {cityChoices.map(c => <option key={c} value={c} />)}
          </datalist>
        )}
      </Field>
      <Field label="State / Region">
        <Input value={value.state_region} onChange={e => onChange('state_region', e.target.value)} data-testid={`${testIdPrefix}-state`} />
      </Field>
      <Field label="Country">
        <Input list={countryOptionsId} value={value.country} onChange={e => onChange('country', e.target.value)} data-testid={`${testIdPrefix}-country`} placeholder="Start typing…" />
        <datalist id={countryOptionsId}>
          {COUNTRIES.map(c => <option key={c} value={c} />)}
        </datalist>
      </Field>
      <Field label="Postcode / ZIP">
        <Input value={value.postcode} onChange={e => onChange('postcode', e.target.value)} data-testid={`${testIdPrefix}-postcode`} />
      </Field>
    </div>
  );
};
const ContactFields = ({ value, setListItem, required, phoneOnly, testIdPrefix }) => (
  <>
    <Grid2>
      <Field label={`Full Name${required ? ' *' : ''}`}><Input value={value.full_name} onChange={e => setListItem('full_name', e.target.value)} data-testid={`${testIdPrefix}-name`} /></Field>
      <Field label={`Phone${required ? ' *' : ''}`}><Input value={value.phone} onChange={e => setListItem('phone', e.target.value)} data-testid={`${testIdPrefix}-phone`} /></Field>
      <Field label={`Email${required && !phoneOnly ? ' *' : ''}`}><Input type="email" value={value.email} onChange={e => setListItem('email', e.target.value)} data-testid={`${testIdPrefix}-email`} /></Field>
      <Field label="Alternate Contact"><Input value={value.alt_contact} onChange={e => setListItem('alt_contact', e.target.value)} placeholder="Second phone or WhatsApp" /></Field>
    </Grid2>
    <Subheader>Address</Subheader>
    <AddressFields value={value.address} onChange={(k, v) => setListItem(`address.${k}`, v)} testIdPrefix={`${testIdPrefix}-addr`} />
  </>
);
